import { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'wouter';
import { useToggle, useMap } from '@mantine/hooks';
import {
  Container,
  Paper,
  Title,
  TextInput,
  PasswordInput,
  Button,
  Text,
  Stack,
  Group,
  Anchor,
} from '@mantine/core';
import { IconAt, IconLock, IconPhone } from '@tabler/icons-react';
import { useAuthHook } from '../hooks/auth.hooks';
import PricingPage from '../components/stripe.price.table';
import { notifications } from '@mantine/notifications';
import { PasswordInputWithRequirements } from '@/componets/password.requirements.tsx';

export function EntryPage() {
  const { signin, signup, isAuthenticated } = useAuthHook();
  const [loading, setLoading] = useState(false);
  const [, setLocation] = useLocation();
  const [searchParams,] = useSearchParams();

  const [showPricing, setShowPricing] = useState(false);
  const [userID, setUserID] = useState('');
  const [pageMode, toggleMode] = useToggle(['signin', 'signup']);
  // UI element states
  const entryForm = useMap<string, string>([
    ['login', ''],
    ['password', ''],
    ['irlName', ''],
    ['companyName', ''],
    ['email', ''],
    ['phone', ''],
  ]);
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const startingMode = searchParams.get('startin') as 'signin' | 'signup' | null;
    toggleMode(startingMode || 'signin');
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      if (pageMode === 'signup') {
        setShowPricing(true);
        return;
      }
      setLocation('/dashboard');
    }
  }, [isAuthenticated]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let aid = ''; // The accessor ID
      if (pageMode === 'signin') {
        aid = await signin({
          login: entryForm.get('login')!,
          password: entryForm.get('password')!,
        })
      } else {
        if (entryForm.get('password') !== confirmPassword) {
          console.error('Passwords do not match');
          return;
        }
        const phone = (entryForm.get('phone')!).replace(/\D/g, '');

        aid = await signup({
          password: entryForm.get('password')!,
          irlName: entryForm.get('irlName')!,
          email: entryForm.get('email')!,
          companyName: entryForm.get('companyName')!,
          phone,
        })
      }
      setUserID(aid);
    } catch (error) {
      notifications.show({
        title: `Sign ${pageMode === 'signin' ? 'in' : 'up'} failed`,
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container size={showPricing ? '100vw' : 'xs'} py="xl">
      <Paper radius="md" p={showPricing ? 0 : 'xl'} withBorder>
        <Title order={2} ta="center" mb="lg">
          Welcome to My Yenta
        </Title>

        {showPricing ? <PricingPage planSelect={searchParams.get('plansele')} /> : <Stack>
          {pageMode === 'signin' && <TextInput
            label="Login Name"
            description="Sign in with your email or phone number"
            placeholder="your@email.com"
            leftSection={<IconAt size={16} />}
            value={entryForm.get('login')}
            onChange={(e) => entryForm.set('login', e.target.value)}
          />}
          {pageMode === 'signup' && (<>
            <TextInput
              required
              label="Your Name"
              placeholder="Steven Doe"
              value={entryForm.get('irlName')}
              onChange={(e) => entryForm.set('irlName', e.target.value)}
            />
            <TextInput label="Company Name" placeholder="Doe Group Inc" value={entryForm.get('companyName')} onChange={(e) => entryForm.set('companyName', e.target.value)} />
            <TextInput
              required
              label="Email"
              placeholder="your@email.com"
              leftSection={<IconAt size={16} />}
              value={entryForm.get('email')}
              onChange={(e) => entryForm.set('email', e.target.value)}
            />
            <TextInput
              required
              label="Phone"
              placeholder="123 321 1122"
              type='tel'
              leftSection={<IconPhone size={16} />}
              value={entryForm.get('phone')}
              onChange={(e) => entryForm.set('phone', e.target.value)}
            />
          </>)}

          <PasswordInputWithRequirements
            required
            label="Password"
            placeholder="Your password"
            leftSection={<IconLock size={16} />}
            value={entryForm.get('password')}
            onChange={(e) => entryForm.set('password', e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && pageMode === 'signin') {
                handleSubmit();
              }
            }}
            usereqpanel={pageMode === 'signup'}
            recoverlink={pageMode !== 'signup' ? '/magic/recover' : undefined}
          />

          {pageMode === 'signup' && (
            <PasswordInput
              required
              label="Confirm Password"
              placeholder="Confirm your password"
              leftSection={<IconLock size={16} />}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword !== entryForm.get('password') && confirmPassword !== '' ? 'Passwords do not match' : undefined}
            />
          )}

          <Group justify="space-between" mt="lg">
            <Stack>
              <Text size="lg" c="dimmed" onClick={() => pageMode === 'signin' ? setShowPricing(true) : toggleMode('signup')} style={{ cursor: 'pointer' }}>
                {pageMode === 'signin' ? 'Need an account?' : 'Already have an account?'}
              </Text>
            </Stack>

            <Button onClick={handleSubmit} loading={loading}>
              {pageMode === 'signin' ? 'Sign In' : 'Sign Up'}
            </Button>
          </Group>
        </Stack>}
      </Paper>
    </Container>
  );
} 